import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { fetchBitcoinPrice, formatBtcAmount, formatPriceChange } from '../services/bitcoinService';
import { isAuthenticated } from '../services/authService';

const PRESET_AMOUNTS = [
  { credits: 50 },
  { credits: 100 },
  { credits: 250 },
  { credits: 500 },
  { credits: 1000 }
];

// Calculate BTC amount for credits
const calculateBtcAmount = (credits: number, btcPrice: number): number => {
  return credits / btcPrice;
};

const TopupPage = () => {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [btcPrice, setBtcPrice] = useState<number>(60000); // Default fallback price
  const [priceChangePercentage24h, setPriceChangePercentage24h] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check if the user is authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth', { state: { from: '/topup' } });
    }
  }, [navigate]);

  // Fetch the current BTC price from API
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        setIsLoading(true);
        const priceData = await fetchBitcoinPrice();
        setBtcPrice(priceData.currentPrice);
        setPriceChangePercentage24h(priceData.priceChangePercentage24h);
        setLastUpdated(priceData.lastUpdated);
      } catch (error) {
        console.error('Error fetching Bitcoin price:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrice();

    // Refresh price every 5 minutes
    const interval = setInterval(fetchPrice, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  const getTotalAmount = (): number => {
    if (selectedAmount !== null) {
      return selectedAmount;
    }
    return customAmount ? parseFloat(customAmount) : 0;
  };

  const getBtcAmount = (): number => {
    const creditAmount = getTotalAmount();
    return calculateBtcAmount(creditAmount, btcPrice);
  };

  const handleCheckout = () => {
    const amount = getTotalAmount();
    if (amount <= 0) return;

    setIsProcessing(true);

    // Navigate to the crypto checkout page with amount
    navigate(`/checkout/crypto?amount=${amount}&crypto=btc`);
  };

  // Format the last updated time
  const getLastUpdatedText = (): string => {
    if (!lastUpdated) return 'Updating...';

    const now = new Date();
    const diffMs = now.getTime() - lastUpdated.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  };

  // Determine price trend for styling
  const getPriceTrendClass = (): string => {
    if (priceChangePercentage24h > 0) return 'text-green-500';
    if (priceChangePercentage24h < 0) return 'text-red-500';
    return 'text-[#A3A3A3]';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Buy Credits with Bitcoin</h1>
      <p className="text-[#A3A3A3] mb-8">Add credits to your account using Bitcoin (BTC)</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {/* Amount Selection */}
          <div className="glass p-6 rounded-lg mb-8">
            <h2 className="text-xl font-semibold mb-4">Select Amount</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
              {PRESET_AMOUNTS.map((option) => (
                <div
                  key={option.credits}
                  className={`relative cursor-pointer rounded-lg border-2 ${
                    selectedAmount === option.credits
                      ? 'border-[#7F56D9] bg-[#7F56D9]/10'
                      : 'border-[#2A2633] hover:border-[#7F56D9]/50'
                  } p-4 text-center transition-all`}
                  onClick={() => handleAmountSelect(option.credits)}
                >
                  <p className="text-lg font-bold">{option.credits} Credits</p>
                  <div className="text-xs font-medium text-[#F7931A] bg-[#F7931A]/10 py-0.5 px-2 rounded-full mt-1 inline-block">
                    ₿ {formatBtcAmount(calculateBtcAmount(option.credits, btcPrice))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <label htmlFor="customAmount" className="block text-sm font-medium mb-2">
                Custom Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A3A3A3]">Credits</span>
                <input
                  type="text"
                  id="customAmount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="Enter amount"
                  className={`input pl-20 py-3 w-full bg-[#1F1A27] border ${
                    customAmount ? 'border-[#7F56D9]' : 'border-[#2A2633]'
                  } rounded-lg text-white`}
                />
              </div>
              {customAmount && parseFloat(customAmount) > 0 && (
                <div className="mt-2 text-sm">
                  <span className="text-[#F7931A]">
                    ₿ {formatBtcAmount(parseFloat(customAmount) / btcPrice)} BTC
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Bitcoin Payment Info */}
          <div className="glass p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Payment Method: Bitcoin (BTC)</h2>

            <div className="flex items-center p-4 rounded-lg border-2 border-[#F7931A] bg-[#F7931A]/10">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-3"
                style={{ backgroundColor: `#F7931A20`, color: '#F7931A' }}
              >
                ₿
              </div>
              <div className="flex-grow">
                <span className="text-white font-medium">Bitcoin (BTC)</span>
                <div className="flex items-center">
                  <p className="text-xs text-[#A3A3A3]">Current rate: 1 BTC ≈ ${isLoading ? '...' : btcPrice.toLocaleString()}</p>
                  {!isLoading && (
                    <span className={`text-xs ml-2 ${getPriceTrendClass()}`}>
                      {formatPriceChange(priceChangePercentage24h, true)}
                    </span>
                  )}
                </div>
              </div>
              {!isLoading && (
                <div className="text-xs text-[#A3A3A3] ml-auto">
                  Updated: {getLastUpdatedText()}
                </div>
              )}
            </div>

            <div className="mt-4 bg-[#1F1A27]/60 rounded-lg p-4">
              <h3 className="font-medium mb-2 flex items-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-[#F59E0B] mr-2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z"/>
                </svg>
                Important Information
              </h3>
              <ul className="text-sm text-[#A3A3A3] space-y-2">
                <li>• Credits can be redeemed back to Bitcoin at any time from your account.</li>
                <li>• The same exchange rate will be used for both buying and redeeming credits.</li>
                <li>• Bitcoin transactions may take time to confirm on the blockchain.</li>
                <li>• The BTC price shown is updated regularly from market data.</li>
                <li>• Price at checkout may differ slightly due to market fluctuations.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="glass p-6 rounded-lg sticky top-24">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-[#A3A3A3]">Credits</span>
                <span>{getTotalAmount()} Credits</span>
              </div>

              <div className="h-px bg-[#2A2633]"></div>

              <div className="flex justify-between items-center">
                <span className="text-[#A3A3A3]">BTC Amount</span>
                <span className="flex items-center font-mono">
                  <span className="text-[#F7931A] mr-1 font-bold">₿</span>
                  {formatBtcAmount(getBtcAmount())}
                </span>
              </div>

              {!isLoading && (
                <div className="text-xs text-[#A3A3A3] text-right">
                  ≈ ${getTotalAmount().toFixed(2)} at current rate
                </div>
              )}
            </div>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={getTotalAmount() <= 0 || isProcessing || isLoading}
              onClick={handleCheckout}
            >
              {isLoading ? 'Loading Rates...' : isProcessing ? 'Processing...' : 'Proceed to Checkout'}
            </Button>

            <p className="text-xs text-[#A3A3A3] mt-4">
              By clicking "Proceed to Checkout", you agree to our <a href="/terms" className="text-[#7F56D9]">Terms of Service</a> and <a href="/privacy" className="text-[#7F56D9]">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopupPage;
